/**
 * Graph → recipe serializer (introspection).
 *
 * Produces the canonical, compact recipe form of a live graph — the SAME
 * language buildGraphFromRecipe consumes, so introspection output is valid
 * generation input (round-trippable; see round-trip.unit.test.ts).
 *
 * Token rules (spec §T1/§T3): deterministic output (prompt-cache friendly),
 * defaults omitted, no positions/ids/transient editor state.
 */
import { UniversalNodeRegistry } from '@/features/backtest/schemas/universal-node-schema'
import type { UniversalNodeSchema } from '@/features/backtest/schemas/universal-node-schema'
import type { GraphNode, GraphEdge } from '@/features/backtest/schemas/backtest-types'
import type { Recipe, RecipeStep } from '@/features/backtest/utils/recipe-types'
import { normalizeCustomName } from '@/features/backtest/utils/node-display-name'
import type { MaterializedInput } from './types'

const OHLCV_FIELDS = new Set(['open', 'high', 'low', 'close', 'volume'])

export interface SerializeResult {
  recipe: Recipe
  /** stepId → runtime node id; the engine's bridge for edit ops. */
  idMap: Map<string, string>
}

export function graphToRecipe(nodes: GraphNode[], edges: GraphEdge[]): SerializeResult {
  const ordered = canonicalOrder(nodes, edges)

  // Assign human-readable step ids in canonical order
  const stepIdByNodeId = new Map<string, string>()
  const idMap = new Map<string, string>()
  const counters = new Map<string, number>()
  for (const node of ordered) {
    const slug = (node.type || 'node').toLowerCase()
    const n = (counters.get(slug) ?? 0) + 1
    counters.set(slug, n)
    const stepId = n === 1 ? slug : `${slug}_${n}`
    stepIdByNodeId.set(node.id, stepId)
    idMap.set(stepId, node.id)
  }

  const edgesByTarget = new Map<string, GraphEdge[]>()
  for (const edge of edges) {
    const list = edgesByTarget.get(edge.target) ?? []
    list.push(edge)
    edgesByTarget.set(edge.target, list)
  }

  const nodeById = new Map(nodes.map((n) => [n.id, n]))
  const recipe: Recipe = ordered.map((node) =>
    serializeStep(node, edgesByTarget.get(node.id) ?? [], nodeById, stepIdByNodeId),
  )

  return { recipe, idMap }
}

// ── Canonical ordering ──────────────────────────────────────

/**
 * Topological order with a deterministic ready-set sort:
 * primary priceData first (the builder marks the FIRST priceData primary, so
 * order is load-bearing), then type, then node id. Input array order never
 * affects the output.
 */
function canonicalOrder(nodes: GraphNode[], edges: GraphEdge[]): GraphNode[] {
  const byId = new Map(nodes.map((n) => [n.id, n]))
  const inDegree = new Map<string, number>(nodes.map((n) => [n.id, 0]))
  const downstream = new Map<string, string[]>()

  for (const edge of edges) {
    if (!byId.has(edge.source) || !byId.has(edge.target)) continue
    inDegree.set(edge.target, (inDegree.get(edge.target) ?? 0) + 1)
    const list = downstream.get(edge.source) ?? []
    list.push(edge.target)
    downstream.set(edge.source, list)
  }

  const rank = (n: GraphNode): string => {
    const primary = n.data?.isPrimary === true ? '0' : '1'
    return `${primary}|${n.type}|${n.id}`
  }

  const ready = nodes.filter((n) => (inDegree.get(n.id) ?? 0) === 0)
  const ordered: GraphNode[] = []
  const seen = new Set<string>()

  while (ready.length > 0) {
    ready.sort((a, b) => (rank(a) < rank(b) ? -1 : 1))
    const node = ready.shift()!
    if (seen.has(node.id)) continue
    seen.add(node.id)
    ordered.push(node)
    for (const targetId of downstream.get(node.id) ?? []) {
      const deg = (inDegree.get(targetId) ?? 1) - 1
      inDegree.set(targetId, deg)
      if (deg === 0) ready.push(byId.get(targetId)!)
    }
  }

  // Cycle fallback: append leftovers deterministically (validator reports CYCLE)
  if (ordered.length < nodes.length) {
    const rest = nodes.filter((n) => !seen.has(n.id))
    rest.sort((a, b) => (rank(a) < rank(b) ? -1 : 1))
    ordered.push(...rest)
  }

  return ordered
}

// ── Step serialization ──────────────────────────────────────

function serializeStep(
  node: GraphNode,
  incomingEdges: GraphEdge[],
  nodeById: Map<string, GraphNode>,
  stepIdByNodeId: Map<string, string>,
): RecipeStep {
  const schema = UniversalNodeRegistry.getSchemaResolved(node.type)

  const inputs: Record<string, string | number | boolean> = {}
  const extraParams: Record<string, unknown> = {}
  const derivedDUModes = new Map<string, 'node' | 'static'>()

  const inputEntries = Array.isArray(node.data?.inputs)
    ? (node.data.inputs as MaterializedInput[])
    : []
  const edgeByHandle = new Map<string, GraphEdge>()
  for (const e of incomingEdges) {
    if (e.targetHandle) edgeByHandle.set(e.targetHandle, e)
  }

  const handledHandles = new Set<string>()

  for (const entry of inputEntries) {
    if (!entry?.name) continue
    handledHandles.add(entry.name)
    const inputDef = schema?.inputs.find((i) => i.id === entry.name)

    if (entry.inputType === 'static') {
      if (inputDef?.discriminatedUnion)
        derivedDUModes.set(inputDef.discriminatedUnion.parameterName, 'static')
      const v = entry.value
      if (typeof v === 'number' || typeof v === 'boolean') {
        inputs[entry.name] = v
      } else if (v !== undefined && v !== null) {
        // String statics can't be literals (strings are refs) — legacy param convention
        extraParams[`${entry.name}_value`] = v
      }
      continue
    }

    const edge = edgeByHandle.get(entry.name)
    if (!edge) continue // unconnected
    const ref = buildRef(edge, entry, nodeById, stepIdByNodeId)
    if (ref) {
      inputs[entry.name] = ref
      if (inputDef?.discriminatedUnion)
        derivedDUModes.set(inputDef.discriminatedUnion.parameterName, 'node')
    }
  }

  // Edges whose handle has no inputs-array entry (legacy graphs / dynamic handles)
  const orphanHandles = [...edgeByHandle.keys()].filter((h) => !handledHandles.has(h)).sort()
  for (const handle of orphanHandles) {
    const ref = buildRef(edgeByHandle.get(handle)!, undefined, nodeById, stepIdByNodeId)
    if (ref) inputs[handle] = ref
  }

  const params = serializeParams(node, schema, derivedDUModes, extraParams)

  const step: RecipeStep = {
    id: stepIdByNodeId.get(node.id)!,
    type: node.type,
  }
  if (params && Object.keys(params).length > 0) step.params = params
  if (Object.keys(inputs).length > 0) step.inputs = inputs
  // The user's name for the node (utils/node-display-name); code mode prints it as `// @name`.
  const name = normalizeCustomName(node.data?.customName)
  if (name) step.name = name
  return step
}

function buildRef(
  edge: GraphEdge,
  entry: MaterializedInput | undefined,
  nodeById: Map<string, GraphNode>,
  stepIdByNodeId: Map<string, string>,
): string | null {
  const sourceNode = nodeById.get(edge.source)
  const sourceStepId = stepIdByNodeId.get(edge.source)
  if (!sourceNode || !sourceStepId) return null

  const sourceSchema = UniversalNodeRegistry.getSchemaResolved(sourceNode.type)
  const sourceHandle = edge.sourceHandle ?? sourceSchema?.outputs[0]?.id
  if (!sourceHandle) return null

  // OHLCV sugar: ohlcv-typed output + a field selection on the receiving input
  const output = sourceSchema?.outputs.find((o) => o.id === sourceHandle)
  if (output?.type === 'ohlcv') {
    const selected = entry?.field ?? entry?.source
    const field = typeof selected === 'string' && OHLCV_FIELDS.has(selected) ? selected : 'close'
    return `${sourceStepId}.${field}`
  }

  return `${sourceStepId}.${sourceHandle}`
}

// ── Parameter serialization ─────────────────────────────────

/** Normalize descriptor-array or record parameters into a record. */
function paramsToRecord(raw: unknown): Record<string, unknown> {
  if (Array.isArray(raw)) {
    const out: Record<string, unknown> = {}
    for (const p of raw) {
      if (p && typeof p === 'object' && 'name' in p) {
        out[(p as { name: string }).name] = (p as { value?: unknown }).value
      }
    }
    return out
  }
  if (raw && typeof raw === 'object') return raw as Record<string, unknown>
  return {}
}

/** Loose equality so "20" (editor string) and 20 (schema default) both omit. */
function looselyEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true
  if (a == null || b == null) return false
  if (typeof a !== 'object' && typeof b !== 'object') return String(a) === String(b)
  return false
}

function serializeParams(
  node: GraphNode,
  schema: UniversalNodeSchema | undefined,
  derivedDUModes: Map<string, 'node' | 'static'>,
  extraParams: Record<string, unknown>,
): Record<string, unknown> | undefined {
  const record = paramsToRecord(node.data?.parameters)
  const out: Record<string, unknown> = {}

  if (schema) {
    for (const def of schema.parameters) {
      const value = record[def.name]
      if (value === undefined) continue
      // Discriminated-union mode params are derived from the inputs we emit
      const derived = derivedDUModes.get(def.name)
      if (derived !== undefined) {
        if (!looselyEqual(value, derived)) out[def.name] = value
        continue
      }
      if (looselyEqual(value, def.default)) continue
      out[def.name] = value
    }
  } else {
    // Unknown schema: emit everything primitive (validator flags UNKNOWN_TYPE)
    for (const [k, v] of Object.entries(record)) {
      if (v !== undefined && typeof v !== 'object' && typeof v !== 'function') out[k] = v
    }
  }

  // Static-mode DU params whose mode the builder can't derive from a literal
  // (string statics) still need the explicit 'static' mode param
  for (const [paramName, mode] of derivedDUModes) {
    if (mode === 'static' && Object.keys(extraParams).length > 0) {
      const def = schema?.parameters.find((p) => p.name === paramName)
      if (def && !looselyEqual('static', def.default) && out[paramName] === undefined) {
        out[paramName] = 'static'
      }
    }
  }

  Object.assign(out, extraParams)
  return Object.keys(out).length > 0 ? out : undefined
}
